import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader, Tag } from "@threadws/jute";
import Markdown from "react-markdown";
import type { AgentRun, AgentRunStatus } from "../../api/generated";
import { agentRunsListOptions, agentRunsListQueryKey, agentsVersionsOptions } from "../../api/generated/@tanstack/react-query.gen";
import { CHANNELS } from "../../realtime/ablyClient";
import { useChannel } from "../../realtime/useChannel";

const TONES: Record<AgentRunStatus, "neutral" | "accent" | "success" | "danger"> = {
  queued: "neutral",
  running: "accent",
  succeeded: "success",
  failed: "danger",
};

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
const seconds = (run: AgentRun) =>
  run.startedAt && run.finishedAt ? `${((Date.parse(run.finishedAt) - Date.parse(run.startedAt)) / 1000).toFixed(1)}s` : null;

/** The agent's latest runs, updated live: what it was asked, which version ran, and its reply or error. */
export function AgentRuns({ agentId }: { agentId: string }) {
  const queryClient = useQueryClient();
  const options = { query: { agentId, limit: 20 } };
  const { data: runs, error } = useQuery(agentRunsListOptions(options));
  const { data: versions } = useQuery(agentsVersionsOptions({ path: { id: agentId } }));
  useChannel(CHANNELS.agentRuns, ({ data }) => {
    if ((data as AgentRun).agentId === agentId) void queryClient.invalidateQueries({ queryKey: agentRunsListQueryKey(options) });
  });
  const versionOf = (id: string) => versions?.find((version) => version.id === id)?.version;

  if (error) return <p className="text-sm text-danger">Couldn't load runs: {error.message}</p>;
  if (!runs) return <Loader label="Loading runs" />;
  if (runs.length === 0) return <p className="text-sm text-text-secondary">No runs yet.</p>;

  return (
    <ol className="flex flex-col">
      {runs.map((run) => (
        <li key={run.id} className="flex flex-col gap-2 border-b border-border-subtle py-3 last:border-b-0">
          <div className="flex flex-wrap items-center gap-2 text-xs text-text-secondary">
            <Tag tone={TONES[run.status]}>{run.status}</Tag>
            <span>{when(run.createdAt)}</span>
            {versionOf(run.promptVersionId) && <span className="font-mono">v{versionOf(run.promptVersionId)}</span>}
            {seconds(run) && <span className="font-mono">{seconds(run)}</span>}
          </div>
          {typeof run.input.text === "string" && <blockquote className="border-l-2 border-border-subtle pl-3 text-sm">{run.input.text}</blockquote>}
          {run.error && <p className="text-sm text-danger">{run.error}</p>}
          {run.output && (
            <div className="flex flex-col gap-2 text-sm text-text-secondary [&_strong]:font-medium [&_ul]:list-disc [&_ul]:pl-5">
              <Markdown>{run.output}</Markdown>
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}
